import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import banner from './../assets/Banner.png';
import logo from './../assets/Logo.png';
import './../Styles/Home.css';
import { FaWhatsapp } from 'react-icons/fa';


const ChevronLeft = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="15 18 9 12 15 6"></polyline>
    </svg>
);

const ChevronRight = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="9 18 15 12 9 6"></polyline>
    </svg>
);

const Home = () => {
    const arrivalsRef = useRef(null);
    const discountsRef = useRef(null);
    const categoriesRef = useRef(null);

    const scroll = (ref, direction) => {
        if (ref.current) {
            const scrollAmount = direction === 'left' ? -400 : 400;
            ref.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
        }
    };
    const [isVisible, setIsVisible] = useState(false);
    const [newArrivals, setNewArrivals] = useState([]);
    const [discountItems, setDiscountItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [whatsappLink, setWhatsappLink] = useState('');
    const [featuredCoupon, setFeaturedCoupon] = useState(null);
    const [showCouponModal, setShowCouponModal] = useState(false);


    const [scrollState, setScrollState] = useState({
        arrivals: { left: false, right: true },
        discounts: { left: false, right: true },
        categories: { left: false, right: true }
    });

    const [isDesktop, setIsDesktop] = useState(false);

    useEffect(() => {
        const check = () => setIsDesktop(window.innerWidth >= 992);
        check();
        window.addEventListener('resize', check);
        return () => window.removeEventListener('resize', check);
    }, []);

    const updateScrollState = (ref, section) => {
        if (ref.current) {
            const { scrollLeft, scrollWidth, clientWidth } = ref.current;
            setScrollState(prev => ({
                ...prev,
                [section]: {
                    left: scrollLeft > 10,
                    right: scrollLeft + clientWidth < scrollWidth - 10
                }
            }));
        }
    };

    useEffect(() => {
        const refs = [
            { ref: arrivalsRef, name: 'arrivals' },
            { ref: discountsRef, name: 'discounts' },
            { ref: categoriesRef, name: 'categories' }
        ];

        const handleScroll = () => {
            refs.forEach(r => updateScrollState(r.ref, r.name));
        };

        // Initial check after a short delay to ensure rendering is complete
        const timer = setTimeout(handleScroll, 500);

        refs.forEach(r => {
            if (r.ref.current) {
                r.ref.current.addEventListener('scroll', handleScroll);
            }
        });

        window.addEventListener('resize', handleScroll);

        return () => {
            clearTimeout(timer);
            refs.forEach(r => {
                if (r.ref.current) {
                    r.ref.current.removeEventListener('scroll', handleScroll);
                }
            });
            window.removeEventListener('resize', handleScroll);
        };
    }, [loading, newArrivals, discountItems, categories]);

    useEffect(() => {
        setIsVisible(true);
        fetchHomeData();
    }, []);

    const getDirectWhatsappLink = (link) => {
        if (!link) return '';
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        if (isMobile && link.includes('wa.me/')) {
            const number = link.split('wa.me/')[1];
            return `whatsapp://send?phone=${number}`;
        }
        return link;
    };

    const getImageUrl = (path) => {
        if (!path) return '';
        const cleanPath = path.replace(/\\/g, '/');
        return cleanPath.startsWith('/') ? `${API_BASE_URL}${cleanPath}` : `${API_BASE_URL}/${cleanPath}`;
    };

    const fetchHomeData = async () => {
        const cachedData = sessionStorage.getItem('homeData');
        if (cachedData) {
            try {
                const parsed = JSON.parse(cachedData);
                setNewArrivals(parsed.newArrivals || []);
                setDiscountItems(parsed.discountItems || []);
                setCategories(parsed.categories || []);
                setWhatsappLink(parsed.whatsappLink || '');
                setLoading(false);
            } catch (e) {
                console.warn('Failed to parse homeData cache', e);
            }
        }

        try {
            const [prodRes, discRes, catRes, socialRes, couponRes, hiddenRes] = await Promise.all([
                axios.get(`${API_BASE_URL}/admin/products?filterBySeason=true`),
                axios.get(`${API_BASE_URL}/admin/discounted-items?filterBySeason=true`),
                axios.get(`${API_BASE_URL}/admin/categories`),
                axios.get(`${API_BASE_URL}/admin/social-media`),
                axios.get(`${API_BASE_URL}/user/featured-coupon`),
                axios.get(`${API_BASE_URL}/admin/hidden`)
            ]);

            const hiddenList = hiddenRes.data || [];
            const isProductHidden = (prodId) => hiddenList.some(h => h.product_id === prodId && h.variation_id === null && h.isHidden === 1);

            const activeArrivals = Array.isArray(prodRes.data) ? prodRes.data.filter(p => !isProductHidden(p.id)) : [];
            const activeDiscounts = Array.isArray(discRes.data) ? discRes.data.filter(p => {
                if (isProductHidden(p.id)) return false;
                if (p.variation_id) {
                    const isVarHidden = hiddenList.some(h => h.product_id === p.id && h.variation_id === p.variation_id && h.isHidden === 1);
                    if (isVarHidden) return false;
                }
                return true;
            }) : [];

            const finalArrivals = activeArrivals.slice(0, 8);
            const finalDiscounts = activeDiscounts.slice(0, 16);
            const finalCategories = Array.isArray(catRes.data) ? catRes.data : [];
            let wLink = '';

            setNewArrivals(finalArrivals);
            setDiscountItems(finalDiscounts); // Increased to 16 to show more mixed offers/discounts
            setCategories(finalCategories);
            if (socialRes.data && socialRes.data.length > 0) {
                wLink = socialRes.data[0].whatsapp;
                setWhatsappLink(wLink);
            }

            sessionStorage.setItem('homeData', JSON.stringify({
                newArrivals: finalArrivals,
                discountItems: finalDiscounts,
                categories: finalCategories,
                whatsappLink: wLink
            }));

            if (couponRes.data) {
                const coupon = couponRes.data;
                const isLoggedIn = !!localStorage.getItem('token');

                if (coupon.for_new_signup === 1 && isLoggedIn) {
                    // Check if user has orders before showing
                    const userStr = localStorage.getItem('user');
                    if (userStr) {
                        try {
                            const u = JSON.parse(userStr);
                            const orderRes = await axios.get(`${API_BASE_URL}/orders?customer_id=${u.id}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
                            if (orderRes.data && orderRes.data.length === 0) {
                                setFeaturedCoupon(coupon);
                                const hasShown = sessionStorage.getItem('home_coupon_shown');
                                if (!hasShown) {
                                    setTimeout(() => setShowCouponModal(true), 1500);
                                }
                            }
                        } catch (err) {
                            console.error("Error checking orders:", err);
                        }
                    }
                } else {
                    setFeaturedCoupon(coupon);
                    const hasShown = sessionStorage.getItem('home_coupon_shown');
                    if (!hasShown) {
                        setTimeout(() => setShowCouponModal(true), 1500); // Show after 1.5s
                    }
                }
            }

        } catch (error) {
            console.error("Error fetching home data:", error);
        } finally {
            setLoading(false);
        }
    };

    const calculateDiscountedPrice = (price, type, value) => {
        const p = parseFloat(price);
        const v = parseFloat(value);
        if (type === 'percentage') {
            return p - (p * (v / 100));
        } else {
            return p - v;
        }
    };

    return (
        <div style={{ background: '#fff', overflowX: 'hidden' }}>
            {/* Hero Section */}
            <section
                className="hero-section"
                style={isDesktop ? { backgroundImage: `url(${banner})` } : undefined}
            >
                <img src={banner} alt="Banner" className="hero-bg" />
                {/* Overlay with subtle gradient for readability */}
                <div className="hero-overlay" />

                <div
                    className="hero-content"
                    style={{
                        transform: isVisible ? 'translateX(0)' : 'translateX(50px)',
                        opacity: isVisible ? 1 : 0
                    }}
                >
                    <h1 className="hero-title">
                        Mégara <br />
                        <span className="hero-subtitle">Collection</span>
                    </h1>
                    <p className="hero-description">
                        Experience the essence of femininity with our latest curated collection.
                    </p>
                    <Link
                        to="/shop"
                        className="hero-btn"
                    >
                        Shop Now
                    </Link>
                </div>
            </section>

            {/* New Arrivals Section */}
            <section className="home-section-wrapper">
                <div className="home-section-header">
                    <div style={{ marginLeft: isDesktop ? '60px' : '16px' }}>
                        <h2 className="home-section-title">New Arrivals</h2>
                        <p className="home-section-subtitle">
                            The latest additions to our collection
                        </p>
                    </div>
                    <Link to="/shop?sortBy=newest" className="modern-pill-btn" style={{ marginRight: isDesktop ? '60px' : '16px' }}>
                        <span>→</span>
                    </Link>
                </div>

                <div style={{ position: 'relative', marginLeft: isDesktop ? '60px' : '16px', marginRight: isDesktop ? '60px' : '16px' }}>
                    <button className="scroll-arrow" style={{ position: 'absolute', left: '-42px', top: '35%', transform: 'translateY(-50%)', zIndex: 10, display: scrollState.arrivals.left ? 'flex' : 'none' }} onClick={() => scroll(arrivalsRef, 'left')}><ChevronLeft /></button>
                    {loading ? (
                        <div className="home-arrivals-grid">
                            {[1, 2, 3, 4].map(i => (
                                <div key={i} className="home-product-placeholder"></div>
                            ))}
                        </div>
                    ) : (
                        <div className="home-arrivals-grid" ref={arrivalsRef}>
                            {newArrivals.map((product) => (
                                <Link
                                    key={product.id}
                                    to={`/shop`}
                                    style={{ textDecoration: 'none', color: 'inherit' }}
                                >
                                    <div className="home-product-card" style={{
                                        transition: 'all 0.3s ease',
                                        cursor: 'pointer'
                                    }}>
                                        <div style={{
                                            height: '350px',
                                            background: '#f9f9f9',
                                            overflow: 'hidden',
                                            borderRadius: '4px',
                                            position: 'relative',
                                            border: '1px solid #f0f0f0'
                                        }}>
                                            {product.varPhoto || product.photo || product.prodPhoto ? (
                                                <img
                                                    src={getImageUrl(product.varPhoto || product.photo || product.prodPhoto)}
                                                    alt={product.name}
                                                    className="home-product-image"
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover',
                                                        transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
                                                    }}
                                                />
                                            ) : (
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    height: '100%',
                                                    color: '#ccc'
                                                }}>
                                                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                                                        <rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" />
                                                        <polyline points="21 15 16 10 5 21" />
                                                    </svg>
                                                </div>
                                            )}
                                            <div style={{
                                                position: 'absolute',
                                                top: '14px',
                                                left: '14px',
                                                background: '#ff4757',
                                                color: '#fff',
                                                padding: '4px 10px',
                                                fontSize: '0.65rem',
                                                fontWeight: '800',
                                                textTransform: 'uppercase',
                                                letterSpacing: '1.5px',
                                                borderRadius: '3px',
                                                boxShadow: '0 4px 10px rgba(255, 71, 87, 0.3)'
                                            }}>
                                                New
                                            </div>
                                        </div>
                                        <div style={{ padding: '25px 0' }}>
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'flex-start',
                                                marginBottom: '10px'
                                            }}>
                                                <h3 style={{
                                                    fontSize: '1.1rem',
                                                    fontWeight: '700',
                                                    letterSpacing: '-0.3px',
                                                    margin: 0,
                                                    maxWidth: '75%'
                                                }}>{product.name}</h3>
                                                <p style={{
                                                    color: '#000',
                                                    fontWeight: '800',
                                                    fontSize: '1.1rem',
                                                    margin: 0
                                                }}>
                                                    ${parseFloat(product.Price || 0).toFixed(2)}
                                                </p>
                                            </div>
                                            {product.categoryName && (
                                                <p style={{
                                                    color: '#888',
                                                    fontSize: '0.8rem',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '1px',
                                                    margin: 0
                                                }}>{product.categoryName}</p>
                                            )}
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                    <button className="scroll-arrow" style={{ position: 'absolute', right: '-42px', top: '35%', transform: 'translateY(-50%)', zIndex: 10, display: scrollState.arrivals.right ? 'flex' : 'none' }} onClick={() => scroll(arrivalsRef, 'right')}><ChevronRight /></button>
                </div>
            </section>

            {/* Discount Items Section */}
            {discountItems.length > 0 && (
                <div className="home-section-wrapper">
                    <div className="home-section-header">
                        <div style={{ marginLeft: isDesktop ? '60px' : '16px' }}>
                            <h2 className="home-section-title">Limited Offers</h2>
                            <p className="home-section-subtitle">
                                Exclusive discounts and special offers
                            </p>
                        </div>
                        <Link to="/shop?filter=sale" className="modern-pill-btn" style={{ marginRight: isDesktop ? '60px' : '16px' }}>
                            <span>→</span>
                        </Link>
                    </div>

                    <div style={{ position: 'relative', marginLeft: isDesktop ? '60px' : '16px', marginRight: isDesktop ? '60px' : '16px' }}>
                        <button className="scroll-arrow" style={{ position: 'absolute', left: '-42px', top: '35%', transform: 'translateY(-50%)', zIndex: 10, display: scrollState.discounts.left ? 'flex' : 'none' }} onClick={() => scroll(discountsRef, 'left')}><ChevronLeft /></button>
                        <div className="home-discount-grid" ref={discountsRef}>
                            {discountItems.map((product) => (
                                <Link
                                    key={product.product_id || product.discount_id || product.offer_id}
                                    to={`/shop`}
                                    style={{ textDecoration: 'none', color: 'inherit' }}
                                >
                                    <div className="home-product-card" style={{
                                        transition: 'all 0.3s ease',
                                        cursor: 'pointer'
                                    }}>
                                        <div style={{
                                            height: '350px',
                                            background: '#fff',
                                            overflow: 'hidden',
                                            borderRadius: '4px',
                                            position: 'relative',
                                            border: '1px solid #eee'
                                        }}>
                                            {product.displayPhoto || product.varPhoto || product.photo || product.prodPhoto ? (
                                                <img
                                                    src={getImageUrl(product.displayPhoto || product.varPhoto || product.photo || product.prodPhoto)}
                                                    alt={product.name}
                                                    className="home-product-image"
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover',
                                                        transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
                                                    }}
                                                />
                                            ) : (
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    height: '100%',
                                                    color: '#ccc'
                                                }}>
                                                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                                                        <rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" />
                                                        <polyline points="21 15 16 10 5 21" />
                                                    </svg>
                                                </div>
                                            )}
                                            <div style={{
                                                position: 'absolute',
                                                top: '14px',
                                                left: '14px',
                                                background: '#ff4757',
                                                color: '#fff',
                                                padding: '4px 10px',
                                                fontSize: '0.65rem',
                                                fontWeight: '800',
                                                textTransform: 'uppercase',
                                                letterSpacing: '1.5px',
                                                borderRadius: '3px',
                                                boxShadow: '0 4px 10px rgba(255, 71, 87, 0.3)'
                                            }}>
                                                {product.promotion_type === 'offer' ? `Buy ${product.offer_qty} for $${parseFloat(product.offer_price).toFixed(2)}` : (product.discount_type === 'percentage' ? `-${Math.round(product.discount_value)}%` : `-$${product.discount_value}`)}
                                            </div>
                                            {product.variation_id && (
                                                <div style={{
                                                    position: 'absolute',
                                                    bottom: '10px',
                                                    left: '10px',
                                                    background: 'rgba(255,255,255,0.95)',
                                                    color: '#000',
                                                    padding: '4px 10px',
                                                    fontSize: '0.65rem',
                                                    fontWeight: '800',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '1px',
                                                    borderRadius: '2px',
                                                    backdropFilter: 'blur(4px)',
                                                    border: '1px solid #eee'
                                                }}>
                                                    {product.colorName} {product.sizeName}
                                                </div>
                                            )}
                                        </div>
                                        <div style={{ padding: '25px 0' }}>
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'flex-start',
                                                marginBottom: '10px'
                                            }}>
                                                <h3 style={{
                                                    fontSize: '1.1rem',
                                                    fontWeight: '700',
                                                    letterSpacing: '-0.3px',
                                                    margin: 0,
                                                    maxWidth: '65%'
                                                }}>{product.name}</h3>
                                                <div style={{ textAlign: 'right' }}>
                                                    <p style={{
                                                        color: '#ff4757',
                                                        fontWeight: '800',
                                                        fontSize: '1.1rem',
                                                        margin: 0
                                                    }}>
                                                        {product.promotion_type === 'offer' ? `$${parseFloat(product.offer_price).toFixed(2)}` : `$${calculateDiscountedPrice(product.Price, product.discount_type, product.discount_value).toFixed(2)}`}
                                                    </p>
                                                    <p style={{
                                                        color: '#bbb',
                                                        textDecoration: 'line-through',
                                                        fontSize: '0.85rem',
                                                        fontWeight: '600',
                                                        margin: '2px 0 0'
                                                    }}>
                                                        ${product.promotion_type === 'offer' ? (parseFloat(product.Price) * product.offer_qty).toFixed(2) : parseFloat(product.Price).toFixed(2)}
                                                    </p>
                                                </div>
                                            </div>
                                            {product.categoryName && (
                                                <p style={{
                                                    color: '#888',
                                                    fontSize: '0.8rem',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '1px',
                                                    margin: 0
                                                }}>{product.categoryName}</p>
                                            )}
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                        <button className="scroll-arrow" style={{ position: 'absolute', right: '-42px', top: '35%', transform: 'translateY(-50%)', zIndex: 10, display: scrollState.discounts.right ? 'flex' : 'none' }} onClick={() => scroll(discountsRef, 'right')}><ChevronRight /></button>
                    </div>
                </div>
            )}

            {/* Shop by Category Section */}
            {categories.length > 0 && (
                <div className="home-section-wrapper" style={{ paddingBottom: '60px' }}>
                    <div className="home-section-header">
                        <div style={{ marginLeft: isDesktop ? '60px' : '16px' }}>
                            <h2 className="home-section-title">Shop by Category</h2>
                            <p className="home-section-subtitle">
                                Explore our curated collections
                            </p>
                        </div>
                    </div>

                    <div style={{ position: 'relative', marginLeft: isDesktop ? '60px' : '16px', marginRight: isDesktop ? '60px' : '16px' }}>
                        <button className="scroll-arrow" style={{ position: 'absolute', left: '-42px', top: '35%', transform: 'translateY(-50%)', zIndex: 10, display: scrollState.categories.left ? 'flex' : 'none' }} onClick={() => scroll(categoriesRef, 'left')}><ChevronLeft /></button>
                        <div className="home-category-grid" ref={categoriesRef}>
                            {categories.map((category) => (
                                <Link
                                    key={category.id}
                                    to={`/shop?catID=${category.id}`}
                                    style={{
                                        textDecoration: 'none',
                                        color: 'inherit',
                                        position: 'relative',
                                        overflow: 'hidden',
                                        borderRadius: '12px',
                                        height: '300px',
                                        background: '#fcfcfc',
                                        border: '1px solid #f0f0f0',
                                        transition: 'all 0.4s ease'
                                    }}
                                    className="category-card-home-refined"
                                >
                                    <div style={{
                                        width: '100%',
                                        height: '100%',
                                        position: 'relative'
                                    }}>
                                        {category.photo ? (
                                            <>
                                                <img
                                                    src={getImageUrl(category.photo)}
                                                    alt={category.name}
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover',
                                                        transition: 'transform 0.8s ease'
                                                    }}
                                                    className="category-img"
                                                />
                                                {/* Minimal Overlay */}
                                                <div style={{
                                                    position: 'absolute',
                                                    bottom: 0,
                                                    left: 0,
                                                    width: '100%',
                                                    padding: '30px',
                                                    background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)',
                                                    color: '#fff'
                                                }}>
                                                    <h3 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '700', letterSpacing: '-0.5px' }}>{category.name}</h3>
                                                    <span style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1.5px', opacity: 0.9, fontWeight: '600' }}>Explore Collection</span>
                                                </div>
                                            </>
                                        ) : (
                                            <div style={{
                                                width: '100%',
                                                height: '100%',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '20px',
                                                background: '#fcfcfc',
                                                padding: '40px'
                                            }}>
                                                <div style={{
                                                    width: '120px',
                                                    height: '120px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    transition: 'all 0.3s ease'
                                                }} className="category-fallback-logo">
                                                    <img
                                                        src={logo}
                                                        alt="Brand Logo"
                                                        style={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'contain',
                                                            opacity: 0.15,
                                                            filter: 'grayscale(1)'
                                                        }}
                                                    />
                                                </div>
                                                <div style={{ textAlign: 'center' }}>
                                                    <h3 style={{
                                                        margin: 0,
                                                        fontSize: '1.4rem',
                                                        fontWeight: '700',
                                                        color: '#1a1a1a',
                                                        letterSpacing: '-0.5px'
                                                    }}>{category.name}</h3>
                                                    <div style={{
                                                        height: '2px',
                                                        width: '30px',
                                                        background: '#ff4757',
                                                        margin: '12px auto',
                                                        borderRadius: '2px'
                                                    }}></div>
                                                    <span style={{
                                                        fontSize: '0.7rem',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '1.5px',
                                                        color: '#888',
                                                        fontWeight: '700'
                                                    }}>View Collection</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </Link>
                            ))}
                        </div>
                        <button className="scroll-arrow" style={{ position: 'absolute', right: '-42px', top: '40%', transform: 'translateY(-50%)', zIndex: 10, display: scrollState.categories.right ? 'flex' : 'none' }} onClick={() => scroll(categoriesRef, 'right')}><ChevronRight /></button>
                    </div>
                </div>
            )}
            {/* Floating WhatsApp Button */}
            {whatsappLink && (
                <a
                    href={getDirectWhatsappLink(whatsappLink)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="whatsapp-float"

                    style={{
                        position: 'fixed',
                        bottom: '40px',
                        right: '40px',
                        backgroundColor: '#25d366',
                        color: '#fff',
                        width: '60px',
                        height: '60px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '30px',
                        boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                        zIndex: 1000,
                        transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.1)';
                        e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.3)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.2)';
                    }}
                >
                    <FaWhatsapp />
                </a>
            )}
            {/* Featured Coupon Modal */}
            {showCouponModal && featuredCoupon && (
                <div
                    className="modal-overlay"
                    style={{
                        zIndex: 2000,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backdropFilter: 'blur(8px)',
                        background: 'rgba(0,0,0,0.6)'
                    }}
                    onClick={() => {
                        setShowCouponModal(false);
                        sessionStorage.setItem('home_coupon_shown', 'true');
                    }}
                >
                    <div className="coupon-dialog-home" onClick={e => e.stopPropagation()}>
                        <button
                            onClick={() => {
                                setShowCouponModal(false);
                                sessionStorage.setItem('home_coupon_shown', 'true');
                            }}
                            className="coupon-close-btn"
                        >
                            &times;
                        </button>

                        <div style={{ position: 'relative', zIndex: 1 }}>
                            <h2>Special Offer</h2>
                            <p>
                                Enjoy a special discount on your next order. Use the code below at checkout:
                            </p>

                            <div className="coupon-code-container">
                                <span className="coupon-discount-text">
                                    {featuredCoupon.discount_type === 'percentage' ? `${featuredCoupon.discount_value}% OFF` : `$${featuredCoupon.discount_value} OFF`}
                                </span>
                                <span className="coupon-code-value">
                                    {featuredCoupon.code}
                                </span>
                            </div>

                            {featuredCoupon.for_new_signup === 1 && (
                                <p style={{ fontSize: '0.9rem', color: '#f59e0b', marginBottom: '10px', fontWeight: '500' }}>
                                    ✨ Exclusive offer for new customers only
                                </p>
                            )}

                            {featuredCoupon.end_date && (
                                <p style={{ fontSize: '0.85rem', color: '#999', marginBottom: '0' }}>
                                    Valid until {new Date(featuredCoupon.end_date).toLocaleDateString()}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Home;
